import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Mail, Phone, User as UserIcon, MessageSquare, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function AdminInquiries() {
  const { user, loading } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user || user === false) return;
    if (user.role !== "admin") return;
    api.get("/admin/inquiries")
      .then((r) => setInquiries(r.data || []))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load"))
      .finally(() => setLoadingList(false));
  }, [user]);

  if (loading) return <div className="py-32 text-center text-slate-500">Loading…</div>;
  if (!user || user === false) return <Navigate to="/login" replace />;

  if (user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="admin-403">
        <div className="overline mb-3">Restricted</div>
        <h1 className="font-serif text-4xl text-slate-900 mb-4">Admins only</h1>
        <p className="text-slate-600 mb-8">This page is for OC Infraventures staff. Sign in with an admin account.</p>
        <Link to="/" className="inline-block px-6 py-3 border border-slate-200 hover:border-[#D4A437] text-slate-800">Go Home</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-32" data-testid="admin-inquiries-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-12">
        <div className="overline mb-3">Admin · CRM</div>
        <h1 className="font-serif text-4xl sm:text-5xl font-light text-slate-900 flex items-center gap-3">
          <MessageSquare className="w-7 h-7 text-[#D4A437]" strokeWidth={1.5} /> Inquiries
        </h1>
        <p className="text-slate-600 mt-3 max-w-2xl font-light">{inquiries.length} total inquiries from prospective buyers.</p>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
        {error && <div className="border border-red-500/30 bg-red-950/30 text-red-300 p-4 mb-6">{error}</div>}
        {loadingList ? (
          <div className="text-slate-500 py-12">Loading…</div>
        ) : inquiries.length === 0 ? (
          <div className="border border-slate-200 p-12 text-center">
            <h3 className="font-serif text-2xl text-slate-900">No inquiries yet</h3>
          </div>
        ) : (
          <div className="border border-slate-200 divide-y divide-white/5">
            {inquiries.map((inq) => (
              <div key={inq.id} className="p-6 grid md:grid-cols-12 gap-4 hover:bg-slate-50 transition-colors" data-testid={`inq-${inq.id}`}>
                <div className="md:col-span-3">
                  <div className="flex items-center gap-2 text-slate-900">
                    <UserIcon className="w-4 h-4 text-[#D4A437]" strokeWidth={1.5} />
                    <span className="font-medium">{inq.name}</span>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{timeAgo(inq.created_at)}</div>
                </div>
                <div className="md:col-span-3 text-sm text-slate-600 space-y-1">
                  <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-500" /> {inq.email}</div>
                  <div className="flex items-center gap-2"><Phone className="w-3.5 h-3.5 text-slate-500" /> {inq.phone}</div>
                </div>
                <div className="md:col-span-4 text-sm text-slate-700 leading-relaxed">
                  {inq.message || <span className="text-slate-600 italic">No message</span>}
                </div>
                <div className="md:col-span-2">
                  {inq.project_name ? (
                    <Link to={`/projects/${inq.project_id}`} className="text-xs text-[#D4A437] hover:underline flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" /> {inq.project_name}
                    </Link>
                  ) : (
                    <span className="text-xs text-slate-500">General</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
